'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import * as Dialog from '@radix-ui/react-dialog';
import { AppIcon, POPULAR_CATEGORIES, type Workshop, objectPositionCss } from '@impacta/shared';

interface HeaderSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface WorkshopRowProps {
  workshop: Workshop;
  onSelect: () => void;
}

function WorkshopRow({ workshop, onSelect }: WorkshopRowProps) {
  return (
    <Link
      href={`/actividades/${workshop.handle}`}
      onClick={onSelect}
      className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#e6f7f6] transition-colors"
    >
      <div
        className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#FAFAF8] border border-[#E8E8E4] overflow-hidden"
        style={
          workshop.image
            ? {
                backgroundImage: `url(${workshop.image})`,
                backgroundSize: 'cover',
                backgroundPosition: objectPositionCss(workshop.focusX, workshop.focusY),
              }
            : undefined
        }
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#373A36] truncate">{workshop.title}</p>
        <p className="text-xs text-[#6b6e68] truncate">
          {workshop.category}
          {workshop.teacher ? ` · ${workshop.teacher}` : ''}
        </p>
      </div>
      <span className="flex-shrink-0 text-xs font-bold text-[#00A19B]">{workshop.price}</span>
    </Link>
  );
}

function useDebounced<T>(value: T, delay = 150): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function useWorkshops(open: boolean) {
  const [workshops, setWorkshops] = useState<Workshop[] | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    fetch('/api/search/workshops')
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data: { workshops: Workshop[] }) => {
        setWorkshops(data?.workshops ?? []);
      })
      .catch(() => {
        setWorkshops([]);
      })
      .finally(() => setLoading(false));
  }, [open]);

  return { workshops, loading };
}

export default function HeaderSearch({ open, onOpenChange }: HeaderSearchProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 150);
  const inputRef = useRef<HTMLInputElement>(null);
  const { workshops, loading } = useWorkshops(open);

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const trimmed = debouncedQuery.trim().toLowerCase();
  const isSearching = trimmed.length > 0;

  const filtered = useMemo(() => {
    if (!workshops || !isSearching) return [];
    return workshops
      .filter((w) => {
        const haystack = [w.title, w.category, w.teacher].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(trimmed);
      })
      .slice(0, 8);
  }, [workshops, trimmed, isSearching]);

  const featured = useMemo(() => {
    if (!workshops) return [];
    return workshops.slice(0, 3);
  }, [workshops]);

  const close = () => onOpenChange(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    close();
    router?.push(`/actividades?q=${encodeURIComponent(q)}`);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className="fixed inset-0 z-[70] flex md:items-start md:justify-center md:pt-24 md:px-6"
        >
          <div className="bg-white w-full h-[100dvh] md:h-auto md:max-h-[80vh] md:max-w-2xl md:w-full md:rounded-2xl shadow-xl border border-[#E8E8E4] flex flex-col overflow-hidden pb-[env(safe-area-inset-bottom)]">
            <Dialog.Title className="sr-only">Búsqueda de actividades</Dialog.Title>

            {/* Input bar */}
            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 px-4 md:px-5 py-3 border-b border-[#E8E8E4]"
            >
              <AppIcon
                name="MagnifyingGlassIcon"
                size={20}
                className="text-[#6b6e68] flex-shrink-0"
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar actividades, categorías o talleristas"
                aria-label="Buscar actividades"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                className="flex-1 h-12 bg-transparent text-base text-[#373A36] placeholder:text-[#6b6e68] focus:outline-none"
              />
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Cerrar búsqueda"
                  className="p-2 rounded-full text-[#6b6e68] hover:text-[#00A19B] hover:bg-[#e6f7f6] transition-colors"
                >
                  <AppIcon name="XMarkIcon" size={20} />
                </button>
              </Dialog.Close>
            </form>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-3 md:px-4 py-3">
              {!isSearching ? (
                <div className="space-y-5">
                  <section>
                    <h3 className="px-2 text-xs font-bold uppercase tracking-wide text-[#6b6e68] mb-2">
                      Categorías populares
                    </h3>
                    <div className="flex flex-wrap gap-2 px-2">
                      {POPULAR_CATEGORIES.map((cat) => (
                        <Link
                          key={cat}
                          href={`/actividades?category=${encodeURIComponent(cat)}`}
                          onClick={close}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold text-[#373A36] bg-[#FAFAF8] border border-[#E8E8E4] hover:bg-[#e6f7f6] hover:text-[#00A19B] hover:border-[#00A19B] transition-colors"
                        >
                          {cat}
                        </Link>
                      ))}
                    </div>
                  </section>

                  <section>
                    <h3 className="px-2 text-xs font-bold uppercase tracking-wide text-[#6b6e68] mb-2">
                      Actividades destacadas
                    </h3>
                    {loading && !workshops ? (
                      <p className="px-2 text-sm text-[#6b6e68]">Cargando...</p>
                    ) : (
                      <div className="space-y-1">
                        {featured.map((w) => (
                          <WorkshopRow key={w.handle} workshop={w} onSelect={close} />
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              ) : (
                <div>
                  {loading && !workshops ? (
                    <p className="px-2 py-4 text-sm text-[#6b6e68]">Buscando...</p>
                  ) : filtered.length > 0 ? (
                    <div className="space-y-1">
                      {filtered.map((w) => (
                        <WorkshopRow key={w.handle} workshop={w} onSelect={close} />
                      ))}
                    </div>
                  ) : (
                    <div className="px-2 py-8 text-center">
                      <p className="text-sm text-[#373A36] font-semibold">
                        No encontramos actividades para «{query}».
                      </p>
                      <p className="mt-1 text-sm text-[#6b6e68]">
                        Prueba con otra palabra o explora todas las actividades.
                      </p>
                      <Link
                        href="/actividades"
                        onClick={close}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#E0DF00] text-[#373A36] hover:bg-[#00A19B] hover:text-white transition-colors"
                      >
                        Ver todas las actividades
                        <AppIcon name="ArrowRightIcon" size={14} />
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {isSearching && filtered.length > 0 && (
              <Link
                href={`/actividades?q=${encodeURIComponent(query.trim())}`}
                onClick={close}
                className="flex items-center justify-between px-5 py-3 border-t border-[#E8E8E4] text-sm font-semibold text-[#00A19B] hover:bg-[#e6f7f6] transition-colors"
              >
                <span className="truncate">Ver todos los resultados para «{query.trim()}»</span>
                <AppIcon name="ArrowRightIcon" size={16} className="flex-shrink-0 ml-2" />
              </Link>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
